import {useLanding} from "../../store/landing";
import {RatingDisplay} from "../common/RatingDisplay";
import {SourceLogo} from "../common/SourceLogo";
import {useCompare} from "../../store/compare";
import {AddToCart} from "../common/AddToCart";
import {useEffect, useMemo, useRef, useState} from "@wordpress/element";
import {motion} from "framer-motion";
import {useMPE} from "../../hooks/useMPE";
import {useSwipe} from "../../hooks/useSwipe";

const DealCard = ({d, inCompare, onAdd, onRemove, deals, index, onDot}) => {
    const discountPct = d.regularPrice > d.salePrice
        ? Math.round((1 - d.salePrice / d.regularPrice) * 100)
        : 0;
    const {initMpe} = useMPE();

    useEffect(() => {
        if (!d.salePrice) return;
        initMpe({
            mpeSelector: `#deal-mpe-${d.sku}`,
            priceSelector: `#deal-price-${d.sku}`,
            enableMoreInfoLink: false,
        });
    }, [d.sku]);

    return (
        <div
            className="flex flex-col h-full bg-white rounded-[1.5rem] border border-base-200 shadow-sm overflow-hidden p-8 lg:p-16">
            <div className="flex flex-col lg:flex-row gap-12 items-stretch flex-1">
                <div className="w-full lg:w-1/2 flex justify-center">
                    <img
                        src={d.image}
                        alt={d.name}
                        draggable="false"
                        className="h-[350px] max-h-[33vh] w-full max-w-[350px] mx-auto object-contain"
                    />
                </div>
                <div className="w-full lg:w-1/2 flex flex-col flex-1">
                    <div className="flex gap-2 mb-4">
                        {discountPct > 0 && (
                            <span className="bg-[#FE7C22] text-white text-[10px] font-bold px-2 py-1 rounded uppercase">
                                {discountPct}% OFF
                            </span>
                        )}
                        {d.freeShipping && (
                            <span className="bg-dark-blue text-white text-[10px] font-bold px-2 py-1 rounded uppercase">
                                Free Shipping
                            </span>
                        )}
                    </div>
                    <div className="text-[11px] font-black pt-orange mb-1 uppercase tracking-wider">
                        {d.manufacturer}
                    </div>
                    <h3 className="text-2xl font-bold pt-dark-blue mb-2 leading-tight line-clamp-2">
                        {d.name}
                    </h3>
                    <div
                        className="text-[13px] text-base-content/60 mb-6 line-clamp-2 leading-relaxed overflow-hidden"
                        dangerouslySetInnerHTML={{__html: d.description}}
                    />
                    <div className="mt-auto">
                        <div className="flex items-center gap-2 mb-4">
                            <RatingDisplay value={d.customerReviewAverage} size="sm"/>
                            <span className="text-xs font-bold text-base-content/30">({d.customerReviewCount})</span>
                        </div>
                        <div className="flex items-center gap-6">
                            <div className="flex flex-col">
                                <span id={`deal-price-${d.sku}`}
                                      className="text-3xl font-black pt-dark-blue leading-none">${d.salePrice}</span>
                                {d.regularPrice > d.salePrice && (
                                    <span className="text-[11px] text-base-content/40 font-medium">
                                        was <span className="line-through">${d.regularPrice}</span>
                                    </span>
                                )}
                            </div>
                            <div className="h-8">
                                <SourceLogo source={d.source}/>
                            </div>
                        </div>
                        <div id={`deal-mpe-${d.sku}`}
                             className="pt-legal !text-xs text-base-content/50 !p-0 leading-tight mt-0.5 !mb-8"></div>
                        <div className="flex items-center gap-2 mb-2">
                            <AddToCart
                                sku={d.woocommerceId}
                                className="min-w-0 px-8 py-3 h-auto btn-sm flex-1 !text-xs font-bold"
                            />
                            <a
                                href={`/product/${d.woocommerceSlug}`}
                                className="min-w-0 btn btn-sm pt-btn-pill flex-1 pt-btn-clear px-8 py-3 h-auto no-underline !text-xs font-bold border-pt-blue text-pt-blue hover:bg-pt-blue/5"
                            >
                                View
                            </a>
                            <button
                                className={`min-w-0 btn btn-sm pt-btn-pill flex-1 px-8 py-3 h-auto transition-all !text-xs font-bold ${
                                    inCompare ? 'btn-secondary' : 'btn-outline pt-orange border-current hover:bg-secondary/5'
                                }`}
                                onClick={() => inCompare ? onRemove(String(d.sku)) : onAdd(d)}
                            >
                                {inCompare ? <><i className="fa fa-check mr-1"/>Selected</> : 'Compare'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            {deals?.length > 1 && (
                <div className="flex justify-center gap-2 mt-8">
                    {deals.map((_, di) => (
                        <button
                            key={di}
                            onClick={() => onDot?.(di)}
                            className={`w-2 h-2 rounded-full transition-all ${
                                di === index ? 'bg-primary w-4' : 'bg-base-300 hover:bg-base-400'
                            }`}
                            aria-label={`Go to deal ${di + 1}`}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

const FeaturedDealSkeleton = () => (
    <div className="mb-16">
        <div className="skeleton h-10 w-64 mb-8"/>
        <div
            className="flex flex-col h-[600px] bg-white rounded-[1.5rem] border border-base-200 shadow-sm overflow-hidden p-8 lg:p-16">
            <div className="flex flex-col lg:flex-row gap-12 items-stretch flex-1">
                <div className="w-full lg:w-1/2 flex justify-center">
                    <div className="skeleton w-[350px] h-[350px]"/>
                </div>
                <div className="w-full lg:w-1/2 flex flex-col flex-1">
                    <div className="flex gap-2 mb-4">
                        <div className="skeleton h-6 w-20"/>
                        <div className="skeleton h-6 w-24"/>
                    </div>
                    <div className="skeleton h-4 w-24 mb-2"/>
                    <div className="skeleton h-12 w-full mb-4"/>
                    <div className="skeleton h-16 w-full mb-6"/>
                    <div className="mt-auto">
                        <div className="skeleton h-5 w-32 mb-4"/>
                        <div className="flex items-center gap-6 mb-8">
                            <div className="skeleton h-10 w-32"/>
                            <div className="skeleton h-8 w-24"/>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="skeleton h-12 flex-1 rounded-btn"/>
                            <div className="skeleton h-12 flex-1 rounded-btn"/>
                            <div className="skeleton h-12 flex-1 rounded-btn"/>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
);

export const FeaturedDeal = ({loading}) => {
    const {topDeals} = useLanding();
    const {add, remove, contains} = useCompare();
    const [virtualOffset, setVirtualOffset] = useState(0);
    const [containerWidth, setContainerWidth] = useState(0);
    const busy = useRef(false);

    const deals = useMemo(() => topDeals?.[0]?.products || [], [topDeals]);

    const N = deals.length;
    const activeIndex = N > 0 ? ((virtualOffset % N) + N) % N : 0;

    const containerRef = useRef(null);
    const {x, commit, handlePan, handlePanEnd} = useSwipe({
        onNext: () => setVirtualOffset(v => v + 1),
        onPrev: () => setVirtualOffset(v => v - 1),
        containerRef,
    });

    useEffect(() => {
        const measure = () => {
            if (containerRef.current) setContainerWidth(containerRef.current.offsetWidth);
        };
        measure();
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, [deals.length]);

    if (loading) return <FeaturedDealSkeleton/>;
    if (deals.length === 0) return null;

    const getVirtualIndex = (off) => ((virtualOffset + off) % N + N) % N;
    const getVirtualKey = (off) => {
        const cycle = Math.floor((virtualOffset + off) / N);
        return `${deals[getVirtualIndex(off)].sku}-${cycle}`;
    };

    const goTo = (di) => {
        if (busy.current || di === activeIndex) return;
        const diff = di - activeIndex;
        let steps = diff;
        if (Math.abs(diff) > N / 2) steps = diff > 0 ? diff - N : diff + N;
        commit(steps > 0 ? 1 : -1);
    };



    const cp = (d) => ({
        d,
        inCompare: contains(String(d?.sku)),
        onAdd: add,
        onRemove: remove,
    });

    return (
        <div className="mb-16 select-none">
            <h2 className="text-3xl font-black pt-dark-blue mb-8">Deals of the Day</h2>

            <div ref={containerRef} className="relative overflow-hidden w-full">
                {deals.length > 1 && (
                    <>
                        <button
                            onClick={() => commit(-1)}
                            className="absolute left-3 top-1/2 -translate-y-1/2 z-20 btn btn-circle btn-sm btn-ghost bg-base-100/80 shadow-sm"
                            aria-label="Previous deal"
                        >
                            <i className="fa fa-chevron-left"/>
                        </button>
                        <button
                            onClick={() => commit(1)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 z-20 btn btn-circle btn-sm btn-ghost bg-base-100/80 shadow-sm"
                            aria-label="Next deal"
                        >
                            <i className="fa fa-chevron-right"/>
                        </button>
                    </>
                )}

                <motion.div
                    style={{
                        x,
                        display: "flex",
                        width: containerWidth * 3,
                        marginLeft: -containerWidth,
                        touchAction: "pan-y",
                    }}
                    onPan={N > 1 ? handlePan : undefined}
                    onPanEnd={N > 1 ? handlePanEnd : undefined}
                >
                    {[-1, 0, 1].map(offset => {
                        const index = getVirtualIndex(offset);
                        const deal = deals[index];
                        return (
                            <div key={getVirtualKey(offset)} style={{width: containerWidth || "100vw", flexShrink: 0}}>
                                <DealCard
                                    {...cp(deal)}
                                    deals={deals}
                                    index={index}
                                    onDot={goTo}
                                />
                            </div>
                        );
                    })}
                </motion.div>
            </div>
        </div>
    );
};